// Kimiya shopping behaviour — saved products, quick shop, and the product
// page's add to bag.
//
// Three things that all end in the same place (the cart drawer) and all share
// the same tiny add-to-cart helper, so they live together rather than in three
// files that each reimplement the fetch.

(function () {
  'use strict';

  var ADD_URL = (window.routes && window.routes.cart_add_url) || '/cart/add';

  // ---- saved products -----------------------------------------------------
  //
  // Deliberately a two-function interface over one storage driver. There is no
  // wishlist app on this store and the theme cannot write a customer
  // metafield, so the driver is localStorage: real persistence, but tied to
  // one browser on one device. Moving saves onto the customer record means
  // replacing read/write below and nothing else.
  var KEY = 'kimiya:saved:v1';

  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      // Private mode, blocked storage, or a value someone else wrote. An
      // empty list is the right answer in every one of those cases.
      return [];
    }
  }

  function write(items) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(items));
      return true;
    } catch (error) {
      return false;
    }
  }

  var Saved = {
    all: read,
    has: function (handle) {
      return read().some(function (item) {
        return item.handle === handle;
      });
    },
    toggle: function (item) {
      var items = read();
      var index = -1;
      items.forEach(function (existing, i) {
        if (existing.handle === item.handle) index = i;
      });
      if (index > -1) {
        items.splice(index, 1);
      } else {
        // Newest first — a saved list is read as a recent-interest list.
        items.unshift(item);
      }
      write(items);
      document.dispatchEvent(new CustomEvent('kimiya:saved:change', { detail: { items: items } }));
      return index === -1;
    },
    remove: function (handle) {
      var items = read().filter(function (item) {
        return item.handle !== handle;
      });
      write(items);
      document.dispatchEvent(new CustomEvent('kimiya:saved:change', { detail: { items: items } }));
    },
  };

  window.KimiyaSaved = Saved;

  function paintSaveButton(button) {
    var saved = Saved.has(button.dataset.handle);
    button.setAttribute('aria-pressed', String(saved));
    button.setAttribute(
      'aria-label',
      (saved ? 'Remove ' : 'Save ') + (button.dataset.title || 'product') + (saved ? ' from saved' : ' for later')
    );
  }

  function paintAllSaveButtons() {
    document.querySelectorAll('[data-kim-save]').forEach(paintSaveButton);
  }

  // The markup ships in the "not saved" state so the button exists without
  // JavaScript; this is where it catches up with what is actually stored.
  paintAllSaveButtons();
  document.addEventListener('kimiya:saved:change', paintAllSaveButtons);

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-kim-save]');
    if (!button) return;
    // Cards wrap large areas in a link; a heart inside one must not navigate.
    event.preventDefault();
    event.stopPropagation();
    Saved.toggle({
      handle: button.dataset.handle,
      title: button.dataset.title,
      url: button.dataset.url,
      image: button.dataset.image,
    });
  });

  // Another tab saving something should not leave this one out of date.
  window.addEventListener('storage', function (event) {
    if (event.key === KEY) paintAllSaveButtons();
  });

  // ---- add to cart --------------------------------------------------------
  function cartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    return drawer && typeof drawer.renderContents === 'function' ? drawer : null;
  }

  // Resolves with an error string, or null on success. Rejects only on a
  // genuine network failure, which the callers report as a generic message —
  // the alternative is a button that silently does nothing.
  function addToCart(formData) {
    var drawer = cartDrawer();
    if (drawer) {
      var ids = drawer.getSectionsToRender().map(function (section) {
        return section.id;
      });
      formData.append('sections', ids.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    return fetch(ADD_URL + '.js', {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data.status) {
          // Shopify returns 422 with a description for stock and variant
          // problems — that text is more useful than anything invented here.
          return data.description || data.message || 'Could not add to bag.';
        }
        if (drawer) {
          drawer.renderContents(data);
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        }
        return null;
      });
  }

  // ---- quick shop ---------------------------------------------------------
  //
  // A native <dialog>, so the top layer, the backdrop, Escape and focus
  // containment are the browser's job rather than three hundred lines of ours.
  function qsRoot(el) {
    return el.closest('[data-quick-shop]');
  }

  function openQuickShop(root, trigger) {
    var dialog = root.querySelector('[data-qs-dialog]');
    if (!dialog) return;
    root.__opener = trigger;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      // No dialog support: the card still links to the product page, so this
      // is a dead end rather than a broken one.
      dialog.setAttribute('open', '');
    }
  }

  function closeQuickShop(root) {
    var dialog = root.querySelector('[data-qs-dialog]');
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    if (root.__opener) root.__opener.focus();
  }

  function setQuantity(root, value) {
    var input = root.querySelector('[data-qs-qty]');
    if (!input) return;
    var min = parseInt(input.getAttribute('min'), 10) || 1;
    var next = Math.max(min, value);
    input.value = String(next);
  }

  document.addEventListener('click', function (event) {
    var root = qsRoot(event.target);
    if (!root) return;

    var trigger = event.target.closest('[data-qs-trigger]');
    if (trigger) {
      // The card is wrapped in a link; a bare click here would open the dialog
      // and follow the product URL in the same gesture.
      event.preventDefault();
      event.stopPropagation();
      openQuickShop(root, trigger);
      return;
    }

    if (event.target.closest('[data-qs-close]')) {
      closeQuickShop(root);
      return;
    }

    // showModal() stretches the dialog's own box across the backdrop, so a
    // click on the backdrop lands on the dialog element itself.
    if (event.target.matches('[data-qs-dialog]')) {
      closeQuickShop(root);
      return;
    }

    var step = event.target.closest('[data-qs-step]');
    if (step) {
      var input = root.querySelector('[data-qs-qty]');
      setQuantity(root, (parseInt(input.value, 10) || 1) + parseInt(step.getAttribute('data-qs-step'), 10));
      return;
    }

    var option = event.target.closest('[data-qs-variant]');
    if (option) {
      root.querySelectorAll('[data-qs-variant]').forEach(function (button) {
        button.setAttribute('aria-pressed', String(button === option));
      });
      var field = root.querySelector('[data-qs-id]');
      var add = root.querySelector('[data-qs-add]');
      var price = root.querySelector('[data-qs-price]');
      var hint = root.querySelector('[data-qs-hint]');
      if (field) field.value = option.getAttribute('data-qs-variant');
      if (add) add.disabled = false;
      if (hint) hint.hidden = true;
      // The price follows the size, so the header never contradicts the choice.
      if (price && option.getAttribute('data-qs-variant-price')) {
        price.textContent = option.getAttribute('data-qs-variant-price');
      }
      // The was-price is per variant too: one size can be marked down while
      // another is not, and a stale strikethrough is a pricing claim.
      var was = root.querySelector('[data-qs-was]');
      if (was) {
        var wasPrice = option.getAttribute('data-qs-variant-was');
        was.textContent = wasPrice || '';
        was.hidden = !wasPrice;
      }
    }
  });

  // Typing straight into the field can still land below the minimum.
  document.addEventListener('change', function (event) {
    if (!event.target.matches('[data-qs-qty]')) return;
    var root = qsRoot(event.target);
    if (root) setQuantity(root, parseInt(event.target.value, 10) || 1);
  });

  function submitQuickShop(root, form) {
    var add = root.querySelector('[data-qs-add]');
    var label = root.querySelector('[data-qs-add-label]');
    var errorEl = root.querySelector('[data-qs-error]');
    var original = label ? label.textContent : null;

    if (errorEl) errorEl.hidden = true;
    if (add) add.disabled = true;
    if (label) label.textContent = 'adding…';

    addToCart(new FormData(form))
      .then(function (error) {
        if (!error) {
          // Close before the drawer opens, so focus moves cleanly from one
          // modal to the next rather than fighting over it.
          closeQuickShop(root);
          return;
        }
        if (errorEl) {
          errorEl.textContent = error;
          errorEl.hidden = false;
        }
      })
      .catch(function () {
        if (errorEl) {
          errorEl.textContent = 'Could not reach the bag. Please try again.';
          errorEl.hidden = false;
        }
      })
      .finally(function () {
        if (add) add.disabled = false;
        if (label && original !== null) label.textContent = original;
      });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-qs-form]');
    if (!form) return;
    event.preventDefault();
    submitQuickShop(qsRoot(form), form);
  });

  // ---- product page add to bag -------------------------------------------
  //
  // The design's buy form is a plain POST, which navigates to /cart. With the
  // drawer switched on that is the one place a customer would still be thrown
  // out of the page they were reading, so the "add to bag" submit is upgraded
  // in place. "Buy now" is left alone: it carries return_to=/checkout and is
  // meant to leave.
  var buyForm = document.getElementById('kimBuy');

  // Stock and variant problems are reported in the page, next to the button
  // that caused them. A modal alert would block the page and lose the reader's
  // place on a long product page.
  function showBuyError(message) {
    if (!buyForm) return;
    var box = buyForm.querySelector('[data-kim-buy-error]');
    if (!message) {
      if (box) box.hidden = true;
      return;
    }
    if (!box) {
      box = document.createElement('p');
      box.className = 'kim-buy-error';
      box.setAttribute('role', 'alert');
      box.setAttribute('data-kim-buy-error', '');
      var anchor = buyForm.querySelector('.qty-add');
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(box, anchor.nextSibling);
      else buyForm.appendChild(box);
    }
    box.textContent = message;
    box.hidden = false;
  }

  if (buyForm) {
    buyForm.addEventListener('submit', function (event) {
      var submitter = event.submitter || document.getElementById('addBtn');
      if (!submitter || submitter.id !== 'addBtn') return;
      if (!cartDrawer()) return;

      event.preventDefault();
      var original = submitter.innerHTML;
      submitter.disabled = true;

      addToCart(new FormData(buyForm))
        .then(function (error) {
          showBuyError(error);
        })
        .catch(function () {
          // Network trouble — fall back to the real form post rather than
          // leaving the button dead.
          buyForm.submit();
        })
        .finally(function () {
          submitter.disabled = false;
          submitter.innerHTML = original;
        });
    });
  }
  // ---- quick add ----------------------------------------------------------
  //
  // For the places a component is listed with its size already decided — the
  // formula's ingredient rows, where the batch-size tab has chosen the variant
  // for every row at once. There is nothing left to pick, so a dialog would be
  // one more click before the same outcome. Everything else (a card, where the
  // size is still open) keeps the quick shop dialog.
  //
  // The button reports into itself rather than into an error element: these
  // sit in a tight row with no room for a message, and the label is the thing
  // the eye is already on.
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-kim-quick-add]');
    if (!button || button.disabled) return;

    event.preventDefault();

    var id = button.getAttribute('data-variant-id');
    if (!id) return;

    var label = button.querySelector('[data-kim-quick-add-label]') || button;
    var original = label.textContent;
    var restore = function () {
      label.textContent = original;
      button.disabled = false;
    };

    button.disabled = true;
    label.textContent = 'adding…';

    var form = new FormData();
    form.append('id', id);
    form.append('quantity', '1');

    addToCart(form)
      .then(function (error) {
        if (!error) {
          // The drawer opening is the real confirmation; the label only has to
          // survive the moment between the click and the drawer.
          restore();
          return;
        }
        label.textContent = 'unavailable';
        window.setTimeout(restore, 2500);
      })
      .catch(function () {
        label.textContent = 'try again';
        window.setTimeout(restore, 2500);
      });
  });

})();
